import { z } from 'zod';
import { snapshotSchema } from './contracts';
import type { OperationsUpdate } from './contracts';

export class OperationsClientError extends Error {
  constructor(readonly status: number, code?: string) {
    super(status === 401 ? '请先登录后继续。' : status === 403 ? '需要管理员权限。' :
      code === 'ENCRYPTION_REQUIRED' ? '服务器未配置加密密钥，暂时无法保存凭据。' :
      code === 'INVALID_INPUT' ? '请检查设置项与 HTTPS 地址。' : '运维设置暂不可用，请重试。');
    this.name = 'OperationsClientError';
  }
}
export function operationsErrorMessage(error: unknown): string {
  return error instanceof OperationsClientError ? error.message : '运维设置暂不可用，请重试。';
}
export class OperationsClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;
  constructor(options: { baseUrl?: string; fetch?: typeof fetch; timeoutMs?: number } = {}) {
    const explicit = options.baseUrl?.trim();
    const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
    this.baseUrl = ((explicit === '' ? undefined : explicit) ?? (configured === '' ? undefined : configured) ?? '/api').replace(/\/+$/, '');
    this.fetcher = options.fetch ?? ((...args) => globalThis.fetch(...args));
    this.timeoutMs = options.timeoutMs ?? 15000;
  }
  private readonly timeoutMs: number;
  private async request<T>(schema: z.ZodType<T>, body?: OperationsUpdate, signal?: AbortSignal): Promise<T> {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, this.timeoutMs);
    try {
      const response = await this.fetcher(`${this.baseUrl}/v1/admin/operations/settings`, {
        method: body === undefined ? 'GET' : 'PUT', credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      let payload: unknown;
      try { payload = await response.json(); }
      catch { throw new OperationsClientError(response.ok ? 0 : response.status); }
      if (!response.ok) {
        const code = payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string' ? payload.error : undefined;
        throw new OperationsClientError(response.status, code);
      }
      return schema.parse(payload);
    } catch (error) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (error instanceof OperationsClientError) throw error;
      throw new OperationsClientError(0);
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  }
  load(signal?: AbortSignal) { return this.request(snapshotSchema, undefined, signal); }
  save(update: OperationsUpdate, signal?: AbortSignal) { return this.request(z.object({ ok: z.literal(true) }).strict(), update, signal); }
}
export const operationsClient = new OperationsClient();
